import React from 'react';
import './ResultsList.scss';
import DotDigits from 'components/redesign/DotDigits';
import Tag from 'components/redesign/Tag';
import ProtivVsehImage from 'assets/images/protiv_vseh.png';

export type ResultRowKind = 'candidate' | 'against-all';

export interface ResultRow {
    kind: ResultRowKind;
    key: string;
    name: string;
    sub?: string;
    picture?: string;
    votes: number;
    percent: number;
    isLeader: boolean;
}
export type ElectionOutcome =
    | { status: 'winner'; rows: ResultRow[]; winner: ResultRow }
    | { status: 'tie'; rows: ResultRow[]; tied: ResultRow[] }
    | { status: 'void'; rows: ResultRow[]; leader: ResultRow };

function Summary({
    outcome,
}: {
    outcome: ElectionOutcome;
}): React.ReactElement {
    if (outcome.status === 'winner') {
        return (
            <div className="rd-results__summary">
                <div className="rd-results__summaryText">
                    <h3 className="rd-results__summaryTitle">
                        Победитель — {outcome.winner.name}
                    </h3>
                </div>
                <Tag label="Победа" tone="done" />
            </div>
        );
    }
    if (outcome.status === 'tie') {
        const names = outcome.tied.map(row => row.name).join(', ');
        return (
            <div className="rd-results__summary">
                <div className="rd-results__summaryText">
                    <h3 className="rd-results__summaryTitle">Ничья: {names}</h3>
                    <p className="rd-results__summaryHint">
                        Повторные выборы назначит студсовет — только между теми,
                        кто разделил первое место.
                    </p>
                </div>
                <Tag label="Ничья" tone="progress" />
            </div>
        );
    }
    return (
        <div className="rd-results__summary">
            <div className="rd-results__summaryText">
                <h3 className="rd-results__summaryTitle">
                    Выборы не состоялись
                </h3>
                <p className="rd-results__summaryHint">
                    «Против всех» набрал больше голосов, чем любой из
                    кандидатов. Повторные выборы назначит студсовет.
                </p>
            </div>
            <Tag label="Не состоялись" tone="locked" />
        </div>
    );
}

function ResultRowView({ row }: { row: ResultRow }): React.ReactElement {
    const className = [
        'rd-results__row',
        row.isLeader ? 'rd-results__row--leader' : '',
    ]
        .filter(Boolean)
        .join(' ');
    return (
        <li className={className}>
            {row.kind === 'candidate' ? (
                <span
                    className="rd-results__avatar"
                    style={{ backgroundImage: `url(${row.picture})` }}
                />
            ) : (
                <span
                    className={[
                        'rd-results__avatar',
                        'rd-results__avatar--against',
                    ].join(' ')}
                    style={{ backgroundImage: `url(${ProtivVsehImage})` }}
                    aria-hidden="true"
                />
            )}
            <span className="rd-results__info">
                <span className="rd-results__name">{row.name}</span>
                {row.sub ? (
                    <span className="rd-results__sub">{row.sub}</span>
                ) : null}
                <span className="rd-results__bar">
                    <span
                        className="rd-results__barFill"
                        style={{ width: `${row.percent}%` }}
                    />
                </span>
            </span>
            <span className="rd-results__votes">
                <DotDigits size={22}>{row.votes}</DotDigits>
                <span className="rd-results__percent">{row.percent}%</span>
            </span>
        </li>
    );
}
function ResultsList({
    outcome,
}: {
    outcome: ElectionOutcome;
}): React.ReactElement {
    return (
        <section className="rd-results">
            <Summary outcome={outcome} />
            <ul className="rd-results__list">
                {outcome.rows.map(row => (
                    <ResultRowView key={row.key} row={row} />
                ))}
            </ul>
        </section>
    );
}

export default React.memo(ResultsList);
